// apps/spa/src/components/reminders/DeleteReminderDialog.tsx
// REMIND-05 — shadcn AlertDialog for delete confirmation.
//
// Per CONTEXT lock:
//   title:   'Delete reminder?'
//   body:    `"<Name>" will be permanently removed. The audit log preserves a record.`
//   cancel:  '[ Cancel ]'
//   confirm: '[ Delete ]' (destructive variant)
//
// On confirm: useDeleteReminder().mutate(reminder.id); the hook fires the green
// '✓ Reminder deleted' toast on success and the red 'Could not delete' toast
// on failure. We close the dialog after the mutation settles.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDeleteReminder } from '@/hooks/useDeleteReminder';
import type { Reminder } from '@guardian/shared';

export interface DeleteReminderDialogProps {
  reminder: Reminder | null;
  open: boolean;
  onClose: () => void;
}

export function DeleteReminderDialog({
  reminder,
  open,
  onClose,
}: DeleteReminderDialogProps) {
  const del = useDeleteReminder();

  function handleConfirm() {
    if (!reminder) return;
    del.mutate(reminder.id, {
      onSuccess: () => onClose(),
      onError: () => onClose(),
    });
  }

  // Render even when reminder is null so the AlertDialog can animate closed
  // gracefully via the open prop. AlertDialog popup is portalled, so an
  // unmount during the in-flight DELETE doesn't disturb the table.
  return (
    <AlertDialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete reminder?</AlertDialogTitle>
          <AlertDialogDescription>
            {reminder
              ? `"${reminder.name}" will be permanently removed. The audit log preserves a record.`
              : ''}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>[ Cancel ]</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={del.isPending}
            onClick={handleConfirm}
          >
            [ Delete ]
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
